import moment from 'moment';
import { fetchMetricsDateRanges } from '@intelligence/v3/services/topCharts';
import { getNullableMoment } from '@intelligence/v3/utils/date';

const RELEASE_DATE_FORMAT = 'YYYY-MM-DD';
const METRIC_DATE_GRANULARITIES: IGranularity[] = ['daily', 'weekly', 'monthly'];
const MOBILE_GAME_DETAIL_RELEASE_METRICS = [
  'sensortower__dau',
  'sensortower__revenue',
  'sensortower__download',
  'sensortower__cumulative_revenue',
  'sensortower__cumulative_downloads',
  'sensortower__retention',
];
const RETENTION_METRIC_REGEXP = /^D\d+$/i;
const MOBILE_STATS_RETENTION_DEFAULT_SEGMENT = 'retention_monthly_sensortower';
const MOBILE_STATS_TAB_METRICS = {
  activeusers: ['sensortower__dau', 'sensortower__wau', 'sensortower__mau'],
  revenue: ['sensortower__revenue'],
  downloads: ['sensortower__download'],
  retention: [
    'sensortower__D2',
    'sensortower__retention',
    'sensortower__D3',
    'sensortower__D7',
  ],
  rpd: [
    'sensortower__rpd',
    'sensortower__cumulative_revenue',
    'sensortower__cumulative_downloads',
  ],
} as const;

type TMobileStatsMainTab = keyof typeof MOBILE_STATS_TAB_METRICS;

const MOBILE_STATS_TAB_ORDER: TMobileStatsMainTab[] = [
  'activeusers',
  'revenue',
  'downloads',
  'retention',
  'rpd',
];

const MOBILE_STATS_ALL_METRICS = Array.from(
  new Set(Object.values(MOBILE_STATS_TAB_METRICS).flat()),
);

const MOBILE_STATS_TAB_URL_SEGMENT: Record<TMobileStatsMainTab, string> = {
  activeusers: 'activeusers',
  revenue: 'revenue',
  downloads: 'downloads',
  retention: MOBILE_STATS_RETENTION_DEFAULT_SEGMENT,
  rpd: 'rpd',
};

const METRIC_DATE_FALLBACK_METRICS: Record<string, string[]> = {
  dau: ['sensortower__dau'],
  revenue: ['sensortower__revenue'],
  download: ['sensortower__download'],
  cumulative_revenue: ['sensortower__cumulative_revenue'],
  cumulative_downloads: ['sensortower__cumulative_downloads'],
  rpd: ['sensortower__cumulative_revenue', 'sensortower__cumulative_downloads'],
  rank: ['sensortower__rank'],
};

interface IMetricDateReleaseInfo {
  releaseTime: string;
  releaseMonth: string;
  releaseWeek: string;
}

interface IEarliestMetricDateInfo {
  source: string;
  metric: string;
  metricDateMetrics: string[];
}

export const getValueList = (value?: string | string[]): string[] => {
  if (Array.isArray(value)) {
    return value.filter(Boolean);
  }
  if (value) {
    return [value];
  }
  return [];
};

const getMetricDateReleaseTime = (data: IMetricDateRange[] | null | undefined): string => {
  const dateRanges = Array.isArray(data) ? data : [];
  const releaseDates = dateRanges
    .map((item) => {
      const momentDate = getNullableMoment(item?.min_date);
      if (momentDate === null) {
        return '';
      }
      return momentDate.format(RELEASE_DATE_FORMAT);
    })
    .filter(Boolean);

  return releaseDates.sort()[0] || '';
};

const getMetricDateMetricCandidates = (metric: string): string[] => {
  const metricDateMetrics = [`sensortower__${metric}`];
  if (RETENTION_METRIC_REGEXP.test(metric)) {
    metricDateMetrics.push('sensortower__retention');
  }

  return Array.from(new Set([...metricDateMetrics, ...(METRIC_DATE_FALLBACK_METRICS[metric] || [])]));
};

const getMetricDateMarkets = (regionType?: string, regions?: string[]): string[] => {
  if (regionType !== 'market' || !Array.isArray(regions)) {
    return [];
  }

  return regions.filter((region) => region && region !== 'global');
};

const getEarliestMetricDateInfos = ({
  dataSources,
  metrics,
}: {
  dataSources?: string[] | string;
  metrics?: string[] | string;
}): IEarliestMetricDateInfo[] => {
  const sourceList = getValueList(dataSources);
  const metricList = getValueList(metrics);
  const normalizedSourceList = sourceList.length > 0 ? sourceList : [''];

  return metricList.reduce<IEarliestMetricDateInfo[]>((infos, metric) => {
    normalizedSourceList.forEach((source) => {
      const [metricSource, ...metricParts] = metric.split('__');
      const hasMetricSource = metricParts.length > 0;
      const metricValue = hasMetricSource ? metricParts.join('__') : metric;
      const sourceValue = hasMetricSource ? metricSource || source : source;
      const metricDateMetrics = getMetricDateMetricCandidates(metricValue);
      if (metricDateMetrics.length === 0) {
        return;
      }

      infos.push({
        source: sourceValue,
        metric: metricValue,
        metricDateMetrics,
      });
    });
    return infos;
  }, []);
};

const getEarliestDateByGranularity = (
  dateRanges: IMetricDateRange[],
  metricDateMetrics: string[],
): Partial<Record<IGranularity, string>> => {
  return dateRanges.reduce<Partial<Record<IGranularity, string>>>((dateMap, item) => {
    const minDate = item?.min_date;
    if (!minDate || !metricDateMetrics.includes(item?.metric || '')) {
      return dateMap;
    }

    const currentDate = dateMap[item.granularity];
    if (!currentDate || minDate < currentDate) {
      dateMap[item.granularity] = minDate;
    }
    return dateMap;
  }, {});
};

const formatMetricDateEarliestDateData = (
  dateRanges: IMetricDateRange[] | null | undefined,
  metricDateInfos: IEarliestMetricDateInfo[],
) => {
  const ranges = Array.isArray(dateRanges) ? dateRanges : [];
  const earliestData = metricDateInfos.reduce<any[]>((dataList, info) => {
    const dateMap = getEarliestDateByGranularity(ranges, info.metricDateMetrics);
    METRIC_DATE_GRANULARITIES.forEach((granularity) => {
      const date = dateMap[granularity];
      if (!date) {
        return;
      }

      dataList.push({
        source: info.source,
        metric: info.metric,
        period: granularity,
        date,
      });
    });
    return dataList;
  }, []);

  return {
    data: {
      earliest_day: earliestData,
      earliest_period: earliestData,
    },
  };
};

export const fetchMobileEarliestDateByMetricDate = async (params: any, signal?: AbortSignal) => {
  const metricDateInfos = getEarliestMetricDateInfos({
    dataSources: params?.data_sources,
    metrics: params?.metric,
  });
  const metricDateMetrics = Array.from(new Set(metricDateInfos.flatMap((info) => info.metricDateMetrics)));

  if (metricDateMetrics.length === 0) {
    return formatMetricDateEarliestDateData([], metricDateInfos);
  }

  const result = await fetchMetricsDateRanges(
    {
      game_ids: params?.unified_id ? [params.unified_id] : [],
      app_ids: params?.app_ids || [],
      granularities: METRIC_DATE_GRANULARITIES,
      markets: getMetricDateMarkets(params?.region_type, params?.regions),
      metrics: metricDateMetrics,
      group_by: [],
      entity_types: ['mobile'],
    },
    signal ? { signal } : undefined,
  );

  return formatMetricDateEarliestDateData(result?.data, metricDateInfos);
};

export const hasMetricMaxDate = (maxDate?: string | null): boolean => {
  return Boolean(maxDate);
};

export const buildMetricMaxDateAvailabilityMap = (
  data: IMetricDateRange[] | null | undefined,
): Map<string, boolean> => {
  const map = new Map<string, boolean>();
  const ranges = Array.isArray(data) ? data : [];

  ranges.forEach((item) => {
    if (!item?.metric || !hasMetricMaxDate(item.max_date)) {
      return;
    }
    map.set(item.metric, true);
  });

  return map;
};

export const hasMobileStatsTabMetricData = (
  tab: TMobileStatsMainTab,
  metricAvailabilityMap: Map<string, boolean>,
): boolean => {
  return MOBILE_STATS_TAB_METRICS[tab].some((metric) =>
    metricAvailabilityMap.get(metric),
  );
};

export const resolveFirstAvailableMobileStatsTabSegment = (
  metricAvailabilityMap: Map<string, boolean>,
): string | null => {
  for (const tab of MOBILE_STATS_TAB_ORDER) {
    if (hasMobileStatsTabMetricData(tab, metricAvailabilityMap)) {
      return MOBILE_STATS_TAB_URL_SEGMENT[tab];
    }
  }
  return null;
};

export const fetchMobileStatsMetricsDateRanges = (
  params: { gameId: string; appIds: string[] },
  signal?: AbortSignal,
) => {
  return fetchMetricsDateRanges(
    {
      game_ids: [params.gameId],
      app_ids: params.appIds,
      granularities: METRIC_DATE_GRANULARITIES,
      markets: [],
      metrics: MOBILE_STATS_ALL_METRICS,
      group_by: [],
      entity_types: ['mobile'],
    },
    signal ? { signal } : undefined,
  );
};
